import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/config";

const WEEKDAYS = ["M", "T", "W", "T", "F", "S", "S"]; // UK week starts Monday
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function iso(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

// Build a Set of booked YYYY-MM-DD day strings from [start, end) ranges.
function buildBookedSet(blocked) {
  const set = new Set();
  for (const b of blocked) {
    const [sy, sm, sd] = b.start.split("-").map(Number);
    const [ey, em, ed] = b.end.split("-").map(Number);
    const cur = new Date(sy, sm - 1, sd);
    const end = new Date(ey, em - 1, ed);
    while (cur < end) {
      set.add(iso(cur.getFullYear(), cur.getMonth(), cur.getDate()));
      cur.setDate(cur.getDate() + 1);
    }
  }
  return set;
}

function MonthGrid({ year, month, bookedSet, todayStr }) {
  const first = new Date(year, month, 1);
  // Monday-first offset: JS getDay() Sun=0..Sat=6 -> Mon=0..Sun=6
  const lead = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div data-testid={`avail-month-${year}-${month + 1}`}>
      <div className="font-display text-xl text-ink mb-3 text-center tracking-wide">
        {MONTH_NAMES[month]} <span className="text-ink/50">{year}</span>
      </div>
      <div className="grid grid-cols-7 gap-y-1.5 text-center">
        {WEEKDAYS.map((w, i) => (
          <div key={`wd-${i}`} className="text-[0.6rem] uppercase tracking-[0.15em] text-ink/45 font-sans pb-1">
            {w}
          </div>
        ))}
        {cells.map((d, i) => {
          if (d === null) return <div key={`e-${i}`} />;
          const ds = iso(year, month, d);
          const booked = bookedSet.has(ds);
          const isToday = ds === todayStr;
          return (
            <div key={ds} className="flex items-center justify-center">
              <span
                className={[
                  "w-8 h-8 flex items-center justify-center font-serif text-sm rounded-full transition-colors",
                  booked
                    ? "text-parchment bg-burgundy/85 line-through decoration-parchment/70"
                    : "text-ink/80",
                  isToday && !booked ? "ring-1 ring-ink/40" : "",
                ].join(" ")}
                data-testid={booked ? `booked-${ds}` : undefined}
              >
                {d}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AvailabilityCalendar() {
  const [blocked, setBlocked] = useState([]);

  useEffect(() => {
    api
      .get("/availability/airbnb")
      .then((r) => setBlocked(r.data?.blocked || []))
      .catch(() => {});
  }, []);

  const bookedSet = useMemo(() => buildBookedSet(blocked), [blocked]);

  const months = useMemo(() => {
    const out = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      out.push({ year: d.getFullYear(), month: d.getMonth() });
    }
    return out;
  }, []);

  const today = new Date();
  const todayStr = iso(today.getFullYear(), today.getMonth(), today.getDate());

  return (
    <div className="max-w-5xl mx-auto" data-testid="availability-calendar">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-10">
        {months.map((m) => (
          <MonthGrid
            key={`${m.year}-${m.month}`}
            year={m.year}
            month={m.month}
            bookedSet={bookedSet}
            todayStr={todayStr}
          />
        ))}
      </div>
      <div className="flex items-center justify-center gap-8 mt-10 font-sans text-[0.68rem] uppercase tracking-[0.2em] text-ink/60">
        <span className="flex items-center gap-2">
          <span className="w-4 h-4 rounded-full border border-ink/30 inline-block" /> Available
        </span>
        <span className="flex items-center gap-2">
          <span className="w-4 h-4 rounded-full bg-burgundy/85 inline-block" /> Booked
        </span>
      </div>
    </div>
  );
}
